import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowRight, Building2, TrendingUp, Home as HomeIcon } from 'lucide-react';
import { featuredProperties, investmentStyles } from '../mock';

const Home = () => {
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [formData, setFormData] = useState({
    investmentStyles: [],
    investmentRange: '',
    investmentTimeline: '',
    name: '',
    email: '',
    phone: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderSticky(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCheckboxChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      investmentStyles: prev.investmentStyles.includes(value) ?
      prev.investmentStyles.filter((item) => item !== value) :
      [...prev.investmentStyles, value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (formData.investmentStyles.length === 0) {
      toast.error('Please select at least one investment style');
      return;
    }

    if (!formData.investmentRange || !formData.investmentTimeline) {
      toast.error('Please select investment range and timeline');
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      if (!backendUrl) {
        throw new Error('REACT_APP_BACKEND_URL not configured');
      }
      
      const response = await fetch(`${backendUrl}/api/leads/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        toast.success('Thank you! We\'ll send you curated opportunities soon.');
        
        // Reset form after successful submission
        setFormData({
          investmentStyles: [],
          investmentRange: '',
          investmentTimeline: '',
          name: '',
          email: '',
          phone: ''
        });
      } else {
        toast.error('Something went wrong. Please try again.');
        console.error('Lead submission failed:', result);
      }
    } catch (error) {
      toast.error('Network error. Please check your connection and try again.');
      console.error('Network error:', error);
    }
  };

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="dark-container">
      {/* Header */}
      <header className={`dark-header ${isHeaderSticky ? 'header-sticky' : ''}`}>
        <div className="header-content">
          <img
            src="https://customer-assets.emergentagent.com/job_dexbit-preview/artifacts/76855p4u_Dexbit%20Logo.png"
            alt="Dexbit Logo"
            className="dark-logo logo-enhanced" />

          <nav className="dark-nav">
            <a href="#opportunities" className="dark-nav-link" onClick={(e) => {e.preventDefault();scrollToSection('opportunities');}}>
              Opportunities
            </a>
            <a href="#how-it-works" className="dark-nav-link" onClick={(e) => {e.preventDefault();scrollToSection('how-it-works');}}>
              How It Works
            </a>
            <a href="#contact" className="dark-nav-link" onClick={(e) => {e.preventDefault();scrollToSection('contact');}}>
              Contact
            </a>
            <Button
              className="btn-primary header-cta"
              onClick={() => scrollToSection('lead-form')}>

              Get Started
              <ArrowRight size={20} />
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-overlay"></div>
        <img
          src="https://images.unsplash.com/photo-1758193431355-54df41421657?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjByZXNpZGVudGlhbCUyMGJ1aWxkaW5nfGVufDB8fHx8MTc3MzkxNDYzNXww&ixlib=rb-4.1.0&q=85"
          alt="Luxury Real Estate"
          className="hero-image" />

        <div className="hero-content">
          <h1 className="hero-headline">
            Dexbit lets Modern Investors<br />Co-own Premium Real Estate
          </h1>
          <p className="hero-subheadline">Combining ownership, access, and returns — with just a small investment.

          </p>
          <div className="hero-cta">
            <Button
              className="btn-primary"
              onClick={() => scrollToSection('investment-styles')}>

              Explore Opportunities
              <ArrowRight size={20} />
            </Button>
            <Button
              className="btn-secondary"
              onClick={() => scrollToSection('lead-form')}>

              Get Started
              <ArrowRight size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* What is Dexbit */}
      <section className="content-section">
        <div className="section-container">
          <h2 className="section-title">A smarter way to own Real Estate</h2>
          <p className="section-description">
            Dexbit is a platform that enables you to invest in and co-own high-quality real estate assets.
            Instead of buying an entire property, you can own a share—giving you access to premium opportunities 
            with lower capital.
          </p>
        </div>
      </section>

      {/* Investment Styles */}
      <section className="content-section" id="investment-styles">
        <div className="section-container">
          <h2 className="section-title">Investment Styles</h2>
          <div className="investment-grid">
            {investmentStyles.map((style) =>
            <Card key={style.id} className="investment-card">
                <div className="investment-image-wrapper">
                  <img src={style.image} alt={style.title} className="investment-image" />
                  <span className="investment-tag">{style.tag}</span>
                </div>
                <div className="investment-content">
                  <h3 className="investment-title">{style.title}</h3>
                  <p className="investment-description">{style.description}</p>
                </div>
              </Card>
            )}
          </div>
          <div className="section-cta">
            <Button
              className="btn-primary"
              onClick={() => scrollToSection('lead-form')}>

              Choose your investment style
              <ArrowRight size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Opportunities */}
      <section className="content-section" id="opportunities">
        <div className="section-container">
          <h2 className="section-title">Featured Opportunities</h2>
          <div className="opportunities-grid">
            {featuredProperties.map((property) =>
            <Card key={property.id} className="opportunity-card">
                <div className="opportunity-image-wrapper">
                  <img src={property.image} alt={property.name} className="opportunity-image" />
                  <span className="opportunity-status">{property.status}</span>
                </div>
                <div className="opportunity-content">
                  <span className="opportunity-type">{property.type}</span>
                  <h3 className="opportunity-name">{property.name}</h3>
                  <p className="opportunity-location">Kokapet, Hyderabad</p>
                  <div className="opportunity-footer">
                    <span className="opportunity-investment">Min. Investment: {property.minInvestment}</span>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="content-section" id="how-it-works">
        <div className="section-container">
          <h2 className="section-title">How It Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">01</div>
              <div className="step-icon">
                <TrendingUp size={32} />
              </div>
              <h3 className="step-title">Choose your investment style</h3>
              <p className="step-description">
                Select from Growth, Income, or Experiential ownership based on your goals
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">02</div>
              <div className="step-icon">
                <Building2 size={32} />
              </div>
              <h3 className="step-title">Invest alongside other Co-owners</h3>
              <p className="step-description">
                Join a community of investors co-owning premium real estate
              </p>
            </div>
            <div className="step-card">
              <div className="step-number">03</div>
              <div className="step-icon">
                <HomeIcon size={32} />
              </div>
              <h3 className="step-title">Earn returns through multiple avenues</h3>
              <p className="step-description">
                Benefit from appreciation, rental income, or strategic exit opportunities
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Dexbit */}
      <section className="content-section">
        <div className="section-container">
          <h2 className="section-title">Why Dexbit</h2>
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <h3 className="benefit-title">Access premium real estate opportunities</h3>
              <p className="benefit-description">
                Invest in high-quality properties typically available only to institutional investors
              </p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <h3 className="benefit-title">Lower capital entry</h3>
              <p className="benefit-description">
                Own a share of premium assets without needing crores of capital
              </p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <h3 className="benefit-title">Transparent investment structure</h3>
              <p className="benefit-description">
                Clear documentation and legal frameworks for peace of mind
              </p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">✓</div>
              <h3 className="benefit-title">Curated, high-quality assets</h3>
              <p className="benefit-description">
                Every property is carefully vetted for quality and investment potential
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="content-section">
        <div className="section-container">
          <h2 className="section-title">Meet the Founders</h2>
          <div className="founders-grid">
            <div className="founder-card">
              <h3 className="founder-name">Srinivas Buddharaju</h3>
              <p className="founder-role">Navy Veteran | Hospitality & Web3 strategist</p>
            </div>
            <div className="founder-card">
              <h3 className="founder-name">Sumeet Issar</h3>
              <p className="founder-role">20+ years in Structural Engineering &Real Estate</p>
            </div>
          </div>
          <p className="legal-note">Legal structuring supported by Hash Legal, Bengaluru</p>
        </div>
      </section>

      {/* Lead Capture Form */}
      <section className="content-section lead-section" id="lead-form">
        <div className="section-container">
          <h2 className="section-title">Tell us what you're looking for</h2>
          <form onSubmit={handleSubmit} className="lead-form">
            <div className="form-section">
              <Label className="form-label">Investment Style (select all that apply)</Label>
              <div className="checkbox-group">
                <div className="checkbox-item">
                  <Checkbox
                    id="growth"
                    checked={formData.investmentStyles.includes('Growth (Residential)')}
                    onCheckedChange={() => handleCheckboxChange('Growth (Residential)')} />

                  <label htmlFor="growth" className="checkbox-label">
                    Growth (Residential)
                  </label>
                </div>
                <div className="checkbox-item">
                  <Checkbox
                    id="income"
                    checked={formData.investmentStyles.includes('Income (Commercial)')}
                    onCheckedChange={() => handleCheckboxChange('Income (Commercial)')} />

                  <label htmlFor="income" className="checkbox-label">
                    Income (Commercial)
                  </label>
                </div>
                <div className="checkbox-item">
                  <Checkbox
                    id="experiential"
                    checked={formData.investmentStyles.includes('Experiential (Hospitality)')}
                    onCheckedChange={() => handleCheckboxChange('Experiential (Hospitality)')} />

                  <label htmlFor="experiential" className="checkbox-label">
                    Experiential (Hospitality)
                  </label>
                </div>
              </div>
            </div>

            <div className="form-section">
              <Label className="form-label">Investment Range</Label>
              <div className="radio-group">
                {['₹5–10L', '₹10–25L', '₹25L+'].map((range) =>
                <label key={range} className="radio-item">
                    <input
                    type="radio"
                    name="investmentRange"
                    value={range}
                    checked={formData.investmentRange === range}
                    onChange={(e) => setFormData({ ...formData, investmentRange: e.target.value })} />

                    <span className="radio-label">{range}</span>
                  </label>
                )}
              </div>
            </div>

            <div className="form-section">
              <Label className="form-label">Investment Timeline</Label>
              <div className="radio-group">
                {['1–3 years', '3–5 years', '5+ years'].map((timeline) =>
                <label key={timeline} className="radio-item">
                    <input
                    type="radio"
                    name="investmentTimeline"
                    value={timeline}
                    checked={formData.investmentTimeline === timeline}
                    onChange={(e) => setFormData({ ...formData, investmentTimeline: e.target.value })} />

                    <span className="radio-label">{timeline}</span>
                  </label>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <Label htmlFor="name" className="form-label">Name *</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required />

              </div>
              <div className="form-field">
                <Label htmlFor="email" className="form-label">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required />

              </div>
            </div>

            <div className="form-field">
              <Label htmlFor="phone" className="form-label">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="+91 XXXXX XXXXX"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required />

            </div>

            <Button type="submit" className="btn-primary form-submit">
              Get curated opportunities
              <ArrowRight size={20} />
            </Button>
          </form>
        </div>
      </section>

      {/* Contact */}
      <section className="content-section" id="contact">
        <div className="section-container contact-section">
          <h3 className="contact-title">Looking for personalized real estate advisory?</h3>
          <p className="contact-subtitle">Reach out to us.</p>
          <a href="mailto:info@dexbit.ai" className="contact-email">info@dexbit.ai</a>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <img
            src="https://customer-assets.emergentagent.com/job_dexbit-preview/artifacts/76855p4u_Dexbit%20Logo.png"
            alt="Dexbit Logo"
            className="footer-logo logo-enhanced" />

          <div className="footer-links">
            <a href="#about" className="footer-link">About</a>
            <a href="#contact" className="footer-link">Contact</a>
          </div>
          <p className="footer-disclaimer">
            Investments in real estate are subject to market risks. Structures may involve co-ownership entities.
          </p>
          <p className="footer-copyright">
            © 2026 Dexbit. All rights reserved.
          </p>
        </div>
      </footer>
    </div>);

};

export default Home;